import { FC, useMemo } from 'react';
import { Icon } from '@kontentino/ui';
import { faBookmark } from '@fortawesome/pro-regular-svg-icons/faBookmark';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faHeart } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faReply } from '@fortawesome/pro-regular-svg-icons/faReply';
import { faRepeat } from '@fortawesome/pro-regular-svg-icons/faRepeat';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { useTranslation } from 'react-i18next';
import type { ContentPost } from 'api/new-analytics/types';
import ReportReactionsCard from './primitives/ReportReactionsCard';
import type { FbReactionId } from 'app/modules/analytics/modules/performance/components/fbReactionsConstants';
import { FB_REACTIONS_ORDER } from 'app/modules/analytics/modules/performance/components/fbReactionsConstants';
import {
  FB_REACTION_TO_METRIC_KEY,
  getFacebookReactionsFromOverview,
} from 'app/modules/analytics/modules/performance/utils/getFacebookReactionsFromOverview';
import { getMetricNumericValue } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import ReportInteractionsRowCard from './primitives/ReportInteractionsRowCard';
import {
  FB_POSTS_COLUMNS,
  FB_REELS_COLUMNS,
  IG_FEED_COLUMNS,
  IG_REELS_COLUMNS,
  IG_STORIES_COLUMNS,
  LI_FEED_COLUMNS,
} from 'app/modules/analytics/modules/content/utils/contentTableColumns';
import ReportContentPostTableCard from './ReportContentPostTableCard';
import type { ReportPlatformBlock } from '../../types/ReportPlatformBlock';

interface Props {
  blocks: ReportPlatformBlock[];
  performanceOverview: Parameters<typeof getFacebookReactionsFromOverview>[0];
  contentPosts: ContentPost[];
  isPerformanceLoading: boolean;
  isContentLoading: boolean;
  selectedPerformanceMetrics?: string[];
}

function filterPostsByEntityTypes(
  posts: ContentPost[],
  entityTypes: string[],
): ContentPost[] {
  return posts.filter((post) => entityTypes.includes(post.entity_type));
}

const ReportPlatformBlocks: FC<Props> = ({
  blocks,
  performanceOverview,
  contentPosts,
  isPerformanceLoading,
  isContentLoading,
  selectedPerformanceMetrics,
}) => {
  const { t } = useTranslation();

  const reactionItems = useMemo(
    () => getFacebookReactionsFromOverview(performanceOverview),
    [performanceOverview],
  );

  const visibleReactionIds = useMemo((): FbReactionId[] | undefined => {
    if (!selectedPerformanceMetrics) {
      return undefined;
    }
    const selected = new Set(selectedPerformanceMetrics);
    const ids = FB_REACTIONS_ORDER.filter((id) =>
      selected.has(FB_REACTION_TO_METRIC_KEY[id]),
    );
    return ids.length > 0 ? ids : undefined;
  }, [selectedPerformanceMetrics]);

  const instagramInteractionItems = useMemo(
    () => [
      {
        id: 'likes',
        icon: <Icon icon={faHeart} />,
        value: getMetricNumericValue(performanceOverview, 'likes'),
        tooltip: t('analyticsPerformance.metrics.igInteractionsLikesTooltip'),
      },
      {
        id: 'comments',
        icon: <Icon icon={faComment} />,
        value: getMetricNumericValue(performanceOverview, 'comments'),
        tooltip: t(
          'analyticsPerformance.metrics.igInteractionsCommentsTooltip',
        ),
      },
      {
        id: 'shares',
        icon: <Icon icon={faShareNodes} />,
        value: getMetricNumericValue(performanceOverview, 'shares'),
        tooltip: t('analyticsPerformance.metrics.igInteractionsSharesTooltip'),
      },
      {
        id: 'saved',
        icon: <Icon icon={faBookmark} />,
        value: getMetricNumericValue(performanceOverview, 'saved'),
        tooltip: t('analyticsPerformance.metrics.igInteractionsSavedTooltip'),
      },
      {
        id: 'replies',
        icon: <Icon icon={faReply} />,
        value: getMetricNumericValue(performanceOverview, 'replies'),
        tooltip: t('analyticsPerformance.metrics.igInteractionsRepliesTooltip'),
      },
      {
        id: 'reposts',
        icon: <Icon icon={faRepeat} />,
        value: getMetricNumericValue(performanceOverview, 'reposts'),
        tooltip: t('analyticsPerformance.metrics.igInteractionsRepostsTooltip'),
      },
    ],
    [performanceOverview, t],
  );

  if (blocks.length === 0) {
    return null;
  }

  return (
    <div className="tw-space-y-3">
      {blocks.map((block) => {
        switch (block.kind) {
          case 'facebook_reactions':
            return (
              <ReportReactionsCard
                key={block.kind}
                items={reactionItems}
                visibleIds={visibleReactionIds}
                align="left"
              />
            );
          case 'instagram_interactions':
            return (
              <ReportInteractionsRowCard
                key={block.kind}
                items={instagramInteractionItems}
                isLoading={isPerformanceLoading}
              />
            );
          case 'facebook_content_posts': {
            const posts = filterPostsByEntityTypes(contentPosts, [
              'post',
              'story',
            ]);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.postsAndStories')}
                columns={FB_POSTS_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          case 'facebook_content_reels': {
            const posts = filterPostsByEntityTypes(contentPosts, ['reel']);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.reels')}
                columns={FB_REELS_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          case 'instagram_content_feed': {
            const posts = filterPostsByEntityTypes(contentPosts, ['post']);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.feedPosts')}
                columns={IG_FEED_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          case 'instagram_content_reels': {
            const posts = filterPostsByEntityTypes(contentPosts, ['reel']);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.reels')}
                columns={IG_REELS_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          case 'instagram_content_stories': {
            const posts = filterPostsByEntityTypes(contentPosts, ['story']);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.stories')}
                columns={IG_STORIES_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          case 'linkedin_content_posts': {
            const posts = filterPostsByEntityTypes(contentPosts, ['post']);
            if (!isContentLoading && posts.length === 0) {
              return null;
            }
            return (
              <ReportContentPostTableCard
                key={block.kind}
                title={t('analyticsContent.sections.feedPosts')}
                columns={LI_FEED_COLUMNS}
                posts={posts}
                isLoading={isContentLoading}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ReportPlatformBlocks;
